import Script from "next/script";
import { useEffect } from "react";
import { useAppSelector } from "../../services/hooks/useReduxHooks";
import {
  removeLeakedGtmTikTokTagText,
  sanitizeTiktokPixelId,
} from "../../utils/help/socialMediaPixels/tiktokPixelHelpers";

const FacebookPixel = () => {
  const { socialMedia } = useAppSelector((state) => state.config);

  useEffect(() => {
    // Initialize dataLayer early to ensure it exists before GTM script loads
    if (typeof window !== "undefined") {
      (window as any).dataLayer = (window as any).dataLayer || [];
    }

    const scriptId = document.getElementById("google-tag-manager");
    const noScriptId = document.getElementById("noscript-google-tag-manager");
    const script = document.createElement("script");
    const noScript = document.createElement("noscript");
    const iFrame = document.createElement("iframe");
    const body = document.body;
    const head = document.head;
    noScript.setAttribute("id", "noscript-google-tag-manager");
    noScript.appendChild(iFrame);

    iFrame.setAttribute(
      "src",
      `https://www.googletagmanager.com/ns.html?id=${socialMedia.googleTagsManager}`
    );
    iFrame.style.display = "none";
    iFrame.setAttribute("width", "0");
    iFrame.setAttribute("height", "0");

    const tagManagerScript = `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${socialMedia.googleTagsManager}')
        `;

    script.setAttribute("id", "google-tag-manager");
    script.textContent = tagManagerScript;
    if (!scriptId && socialMedia.googleTagsManager) {
      head.appendChild(script);
    }

    if (!noScriptId && socialMedia.googleTagsManager) {
      body.prepend(noScript);
    }
  }, [socialMedia.googleTagsManager]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    removeLeakedGtmTikTokTagText();

    const observer = new MutationObserver(() => {
      removeLeakedGtmTikTokTagText();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const existingTikTok = document.getElementById("tiktok-pixel");
    if (existingTikTok) {
      existingTikTok.remove();
    }

    const tiktokPixelId = sanitizeTiktokPixelId(socialMedia.tiktokId);
    if (!tiktokPixelId) {
      return;
    }

    const script = document.createElement("script");
    const head = document.head;
    script.setAttribute("id", "tiktok-pixel");
    script.setAttribute("strategy", "lazyOnload");

    const tikTokPixel = `
           !function (w, d, t) {
  w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};

  ttq.load('${tiktokPixelId}');
  ttq.page();
}(window, document, 'ttq');
        `;

    script.textContent = tikTokPixel;
    head.appendChild(script);

    return () => {
      const el = document.getElementById("tiktok-pixel");
      if (el) {
        el.remove();
      }
    };
  }, [socialMedia.tiktokId]);

  useEffect(() => {
    // Clean up any existing Facebook Pixel elements first
    const existingScript = document.getElementById("fb-pixel");
    const existingNoScript = document.getElementById("noscript-fb-pixel");

    if (existingScript) {
      existingScript.remove();
    }
    if (existingNoScript) {
      existingNoScript.remove();
    }

    // Only proceed if we have a valid fbId
    if (!socialMedia.fbId) {
      return;
    }

    const script = document.createElement("script");
    const noScript = document.createElement("noscript");
    const img = document.createElement("img");
    const head = document.head;
    const body = document.body;

    noScript.setAttribute("id", "noscript-fb-pixel");
    noScript.appendChild(img);

    img.setAttribute("height", "1");
    img.setAttribute("width", "1");
    img.style.display = "none";
    img.setAttribute(
      "src",
      `https://www.facebook.com/tr?id=${socialMedia.fbId}&ev=PageView&noscript=1`
    );

    const facebookPixel = `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            (function() {
              var pid = ${socialMedia.fbId};
              window._prepitFbqPixelInitialized = window._prepitFbqPixelInitialized || {};
              if (window._prepitFbqPixelInitialized[pid]) { return; }
              fbq('init', pid);
              fbq('track', 'PageView');
              window._prepitFbqPixelInitialized[pid] = true;
            })();
        `;

    script.setAttribute("id", "fb-pixel");
    script.innerHTML = facebookPixel;
    head.appendChild(script);
    body.prepend(noScript);

    // Cleanup function to remove elements when component unmounts or dependencies change
    return () => {
      const scriptToRemove = document.getElementById("fb-pixel");
      const noScriptToRemove = document.getElementById("noscript-fb-pixel");

      if (scriptToRemove) {
        scriptToRemove.remove();
      }
      if (noScriptToRemove) {
        noScriptToRemove.remove();
      }
    };
  }, [socialMedia.fbId]);

  return (
    <>
      {/* Google Analytics */}
      {socialMedia.googleAnalytics && (
        <>
          <Script
            id="google-analytics-gtag"
            src={`https://www.googletagmanager.com/gtag/js?id=${socialMedia.googleAnalytics}`}
            strategy="afterInteractive"
          />
          <Script
            id="google-analytics-config"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${socialMedia.googleAnalytics}');
              `,
            }}
          />
        </>
      )}
    </>
  );
};

export default FacebookPixel;
